import { useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { cssVariables, useT, type Theme } from '@skm/ui';
import './auth.css';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function subscribeToScheme(onChange: () => void): () => void {
  if (typeof globalThis.matchMedia !== 'function') return () => undefined;
  const query = globalThis.matchMedia(DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

const prefersDark = () =>
  typeof globalThis.matchMedia === 'function' && globalThis.matchMedia(DARK_QUERY).matches;

/** The browser's colour scheme; before sign-in there is no user preference to read. */
function usePreferredTheme(): Theme {
  return useSyncExternalStore(subscribeToScheme, prefersDark, () => false) ? 'dark' : 'light';
}

/** "SONNET KM" → ["SONNET", "KM"]: the last word is set as the accent line of the wordmark. */
function splitWordmark(name: string): readonly [string, string] {
  const at = name.lastIndexOf(' ');
  return at > 0 ? [name.slice(0, at), name.slice(at + 1)] : [name, ''];
}

/**
 * Branded frame of the pages shown before a session exists (sign-in, TOTP, re-enrolment, invite) [CR-W2d-1]. The
 * page's own `<main className="skm-signin">` is the card; colours are the @skm/ui tokens for the preferred scheme.
 */
export function AuthLayout({ children }: { readonly children: ReactNode }) {
  const t = useT();
  const theme = usePreferredTheme();
  const [name, accent] = splitWordmark(t('frame.app.name'));
  const stages = t('frame.auth.stages')
    .split('·')
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="skm-auth" data-theme={theme} style={cssVariables(theme) as CSSProperties}>
      <aside className="skm-auth__brand">
        <div>
          <p className="skm-auth__wordmark">
            {name} {accent ? <span>{accent}</span> : null}
          </p>
          <p className="skm-auth__tagline">{t('frame.auth.tagline')}</p>
        </div>
        <ol className="skm-auth__tape" aria-hidden="true">
          {stages.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </aside>
      <div className="skm-auth__stage">
        {children}
        <p className="skm-auth__secure">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
            <path
              d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2M3.5 7h9v7h-9z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
          </svg>
          {t('frame.auth.secure')}
        </p>
      </div>
    </div>
  );
}
